import maybe from 'folktale/maybe'
import result from 'folktale/result'
import { isEqual } from 'lodash'
import type {
  Logger,
  EditorSDK,
  I18n,
  SafeUserPreferences,
} from '@wix/wix-data-client-common-standalone'
import { USER_INPUT_FILTER_V1_ROLE } from '@wix/wix-data-client-common-standalone'
import type {
  AppApi,
  DatasetConfig,
  DatasetType,
  EDITOR_TYPE,
  Features,
  InspectorPanelConfig,
} from '@wix/wix-data-client-common'
import {
  viewerTypes,
  GFPP_ACTION_IDS,
  DYNAMIC_PAGE_TYPES,
  getTypeOfProbableDynamicPage,
  ComponentTypes,
  ROUTER_DATASET,
  openGenericConfirmationPanel,
  componentIsUnconfiguredRegularDataset,
  isStudio,
  createDatasetPanel,
} from '@wix/wix-data-client-common'
import { openConnectPanel } from '../panels/connection-config-panel/openPanel'
import { openPanel as openManageContentPanelV2 } from '../panels/manage-content-panel-V2/openPanel'
import removeDisconnectedDatasetFilters from './removeDisconnectedDatasetFilters'
import { logOpenDatasetPanelToBi } from './workerBiEventsLogger'
import { getBindingDefinitions } from '../business-logic/bindings/componentBindingInfo'
import sequence from '@wix/wix-data-client-dbsm-common/src/fp/sequence'
import type { CombinedRouterDatasetConfig } from '../business-logic/datasets/routerDataset'
import { getRouterDatasetConfig } from '../business-logic/datasets/routerDataset'
import { getCollectionName } from '../business-logic/datasets/datasetConfigHelper'
import { getComponentTypeData } from '../business-logic/components/getComponentTypeData'
import { disconnectUserFiltersAndTotalCount } from './disconnectUserFiltersAndTotalCount'
import { getRepeaterAncestor } from '../business-logic/components/getRepeaterAncestor'
import type {
  ComponentRef,
  EventTypeStrings,
  PageRef,
} from '@wix/editor-platform-sdk-types'
import type Experiments from '@wix/wix-experiments'
import type { CollectionsApi } from '@wix/wix-code-collections-api'
import { addDsPanelAddButtonClicked } from '@wix/bi-logger-wix-code-data/v2'
import { SINGLES_PLUGIN_ID } from '@wix/wix-data-schema-types'

const getPayloadProp = (eventPayload: any, prop: string) =>
  maybe.fromNullable(eventPayload && eventPayload[prop])

type DataBindingCustomEvents =
  | 'componentConnectButtonClicked'
  | 'openConnectionsListClicked'

type EventTypes = DataBindingCustomEvents & EventTypeStrings
interface Params {
  appApi: AppApi
  hostname: string
  logger: Logger
  experimentsManager: Experiments
  editorSdkProxy: EditorSDK
  i18n: I18n
  collectionsApi: CollectionsApi
  editorType: EDITOR_TYPE
  safeUserPreferences: SafeUserPreferences
  features: Features
}

export const eventCreator = async ({
  appApi,
  hostname,
  logger,
  experimentsManager,
  editorSdkProxy,
  i18n,
  collectionsApi,
  editorType,
  safeUserPreferences,
  features,
}: Params) => {
  const getControllerCollectionName = async (
    controllerType: DatasetType,
    // DatasetConfig is not the right type to describe a serialized config of a regular dataset
    controllerConfig: DatasetConfig | CombinedRouterDatasetConfig,
    controllerRef: ComponentRef,
  ) => {
    const actualConfig =
      controllerType === ROUTER_DATASET
        ? await getRouterDatasetConfig(
            editorSdkProxy,
            controllerRef,
            controllerConfig as CombinedRouterDatasetConfig,
          )
        : controllerConfig

    return getCollectionName(actualConfig)
  }

  const safeGetComponentType = async (componentRef: any) => {
    try {
      return await editorSdkProxy.components.getType({
        componentRef,
      })
    } catch {
      return null
    }
  }

  const updateLivePreviewIfDatasetChanged = ({
    controllerRef,
  }: {
    controllerRef: ComponentRef
  }) => {
    const oldConfigPromise = appApi.fetchDataset(controllerRef)
    return async () => {
      const { config: oldControllerConfig } = await oldConfigPromise
      const { config: newControllerConfig } =
        await appApi.fetchDataset(controllerRef)
      return !isEqual(oldControllerConfig, newControllerConfig)
        ? editorSdkProxy.document.application.livePreview.refresh({
            shouldFetchData: false,
            source: 'DATASET_CONFIG_CHANGED',
          })
        : null
    }
  }

  const openInspectorInTargetPage = async ({
    targetPage,
    ...inspectorConfig
  }: { targetPage: PageRef } & InspectorPanelConfig) => {
    const currentPage = await editorSdkProxy.pages.getCurrent()
    if (targetPage.id !== currentPage.id) {
      executeOnPageChange(() => appApi.openInspectorPanel(inspectorConfig))
      editorSdkProxy.pages.navigateTo({ pageRef: targetPage })
      return
    }
    appApi.openInspectorPanel(inspectorConfig)
  }

  const executeOnPageChange = (callback: () => void) => {
    const handlePageChange = () => {
      callback()
      //@ts-expect-error no argument expected, find fix for this https://jira.wixpress.com/browse/WD-7839
      editorSdkProxy.removeAllListeners('focusedPageChanged')
    }
    editorSdkProxy.addEventListener('focusedPageChanged', handlePageChange)
  }

  const isComponentInsideConnectedRepeater = async (
    componentRef: ComponentRef,
  ) => {
    const repeaterAncestor = await getRepeaterAncestor({
      componentRef,
      editorSdkProxy,
    })
    return Boolean(repeaterAncestor?.controllerRef)
  }

  const isConnectable = async ({
    componentRef,
    componentSdkType,
    componentViewerType,
  }: {
    componentRef: ComponentRef
    componentSdkType: string
    componentViewerType: string
  }) => {
    const bindingDefinition = await getBindingDefinitions({
      editorSdkProxy,
      appApi,
      componentType: componentSdkType,
      componentViewerType,
      componentRef,
    })

    return maybe.Just.hasInstance(bindingDefinition)
  }

  return async function ({
    eventType,
    eventPayload,
  }: {
    eventType: EventTypes
    eventPayload: any
  }) {
    const getProp = (prop: string) => getPayloadProp(eventPayload, prop)
    const getPropResult = (prop: string) =>
      getPayloadProp(eventPayload, prop).matchWith({
        Nothing: () =>
          result.Error(`[${eventType}] eventPayload is missing ${prop}`),
        Just: ({ value }: { value: any }) => result.Ok(value),
      })
    const { origin } = eventPayload || {}

    switch (eventType) {
      case 'connectedComponentPasted': {
        const { connection, componentRef } = eventPayload

        if (connection.role === USER_INPUT_FILTER_V1_ROLE) {
          editorSdkProxy.controllers.disconnect({
            controllerRef: connection.controllerRef,
            connectToRef: componentRef,
            role: connection.role,
          })
        }

        break
      }

      case 'siteLanguageChanged': {
        appApi.emit('siteLanguageChanged', eventPayload)
        break
      }

      case 'controllerAdded': {
        const { controllerRef } = eventPayload

        if (origin) {
          break
        }

        await removeDisconnectedDatasetFilters({
          controllerRef,
          appApi,
        })

        break
      }

      case 'controllerSettingsButtonClicked':
        logOpenDatasetPanelToBi(
          logger,
          editorSdkProxy,
          collectionsApi,
          eventPayload,
        )
        sequence(
          result,
          ['controllerRef', 'controllerType'].map(getPropResult),
        ).matchWith({
          Error: ({ value: error }: { value: string }) => {
            logger.error(new Error(`Cannot open the dataset config panel`), {
              error,
            })
          },
          Ok: ({
            value: [controllerRef, controllerType],
          }: {
            value: [ComponentRef, DatasetType]
          }) => {
            appApi
              .openDatasetConfigPanel({
                controllerRef,
                controllerConfig:
                  getProp('controllerConfig').getOrElse(undefined),
                controllerDisplayName: getProp(
                  'controllerDisplayName',
                ).getOrElse(undefined),
                controllerType,
              })
              .then(updateLivePreviewIfDatasetChanged({ controllerRef }))
              .catch(error => {
                logger.error(
                  new Error('Failed to open dataset config panel', {
                    cause: error,
                  }),
                )
              })
          },
        })
        break
      case 'controllerGfppClicked':
        switch (eventPayload.id) {
          case GFPP_ACTION_IDS.SETTINGS_ACTION:
            logOpenDatasetPanelToBi(
              logger,
              editorSdkProxy,
              collectionsApi,
              eventPayload,
            )
            sequence(
              result,
              ['controllerRef', 'controllerType', 'controllerConfig'].map(
                getPropResult,
              ),
            ).matchWith({
              Error: ({ value: error }: { value: string }) => {
                logger.error(
                  new Error(`Cannot open the dataset config panel`),
                  {
                    error,
                  },
                )
              },
              Ok: async ({
                value: [controllerRef, controllerType],
              }: {
                value: [ComponentRef, DatasetType]
              }) => {
                appApi
                  .openDatasetConfigPanel({
                    controllerRef,
                    controllerConfig:
                      getProp('controllerConfig').getOrElse(undefined),
                    controllerDisplayName: getProp(
                      'controllerDisplayName',
                    ).getOrElse(undefined),
                    controllerType,
                  })
                  .then(updateLivePreviewIfDatasetChanged({ controllerRef }))
                  .catch(error => {
                    logger.error(
                      new Error(
                        'Failed to open dataset config panel from GFPP',
                        {
                          cause: error,
                        },
                      ),
                    )
                  })
              },
            })
            break
          case GFPP_ACTION_IDS.MANAGE_CONTENT_ACTION:
            sequence(result, [
              getPropResult('controllerType'),
              getPropResult('controllerConfig'),
              getPropResult('controllerRef'),
            ]).matchWith({
              Error: ({ value: error }: { value: string }) => {
                logger.error(new Error(`Cannot open the CM Editor`), {
                  error,
                })
                openGenericConfirmationPanel({
                  editorSdkProxy,
                  logger,
                  t: i18n.t.bind(i18n),
                })
              },
              Ok: async ({
                value: [controllerType, controllerConfig, controllerRef],
              }: {
                value: [
                  DatasetType,
                  DatasetConfig | CombinedRouterDatasetConfig,
                  ComponentRef,
                ]
              }) => {
                try {
                  const collectionName = await getControllerCollectionName(
                    controllerType,
                    controllerConfig,
                    controllerRef,
                  )

                  const schema = await collectionsApi.get(collectionName)
                  const collectionExists = Boolean(schema) && !schema.isDeleted

                  if (collectionExists) {
                    await appApi.openContentManager({
                      collectionId: collectionName,
                      isModal: true,
                      origin: 'dataset_gfpp',
                    })
                  } else {
                    await appApi
                      .openDatasetConfigPanel({
                        controllerRef,
                        controllerConfig,
                        controllerType,
                        controllerDisplayName: getProp(
                          'controllerDisplayName',
                        ).getOrElse(undefined),
                      })
                      .then(
                        updateLivePreviewIfDatasetChanged({ controllerRef }),
                      )
                  }
                } catch (e) {
                  logger.error(
                    new Error('Failed to handle manage content panel action', {
                      cause: e,
                    }),
                  )
                }
              },
            })
            break
        }
        break
      case 'componentConnectButtonClicked': {
        const { componentRef, componentSdkType, origin } = eventPayload

        if (features.inspectorXP) {
          if (componentSdkType === ComponentTypes.Page) {
            return openInspectorInTargetPage({
              targetPage: componentRef,
              view: 'Connect',
              componentRef,
              pageRef: componentRef,
              origin,
            })
          }

          return appApi.openInspectorPanel({
            view: 'Connect',
            componentRef,
            origin,
          })
        }

        return openConnectPanel(
          {
            hostname,
            appApi,
            editorSdkProxy,
            experimentsManager,
            logger,
            i18n,
            editorType,
            initiator: 'manually',
            origin: 'gfpp',
          },
          { componentRef: eventPayload.componentRef },
        )
      }
      case 'componentAddedToStage': {
        const { compRef: componentRef } = eventPayload

        let componentTypeData
        try {
          componentTypeData = await getComponentTypeData({
            editorSdkProxy,
            componentRef,
          })
        } catch {
          break
        }

        const { sdkType: componentSdkType, viewerType: componentViewerType } =
          componentTypeData

        if (componentViewerType === viewerTypes.RefComponent) {
          appApi.refreshControllersState()
          await editorSdkProxy.document.application.livePreview.refresh({
            shouldFetchData: false,
            source: 'REF_COMPONENT_ADDED_TO_STAGE',
          })
        }

        // TODO: Investigate all the issues regarding section component described in this ticket https://jira.wixpress.com/browse/WD-7813
        if (componentSdkType === ComponentTypes.Section) {
          break
        }

        if (features.inspectorXP) {
          const [isUnconfiguredRegularDataset, isInsideRepeater, currentPage] =
            await Promise.all([
              componentIsUnconfiguredRegularDataset({
                editorSdkProxy,
                componentRef,
                componentSdkType,
              }),
              isComponentInsideConnectedRepeater(componentRef),
              editorSdkProxy.pages.getCurrent(),
            ])

          if (isUnconfiguredRegularDataset) {
            const createDatasetPanelResult =
              await appApi.openCreateDatasetPanel({
                componentRef,
                isDatasetTargetingInput: false,
              })

            if (createDatasetPanelResult) {
              await appApi.updateDatasetWithCreateDatasetPanelResult({
                createDatasetPanelResult,
                controllerRef: componentRef,
              })
              const schema = await collectionsApi.get(
                createDatasetPanelResult.collectionId,
              )
              logger.bi(
                addDsPanelAddButtonClicked({
                  origin: 'add_panel',
                  panel_name: createDatasetPanel.panelName,
                  collection_id: createDatasetPanelResult.collectionId,
                  collection_name: schema.displayName,
                  data_set_name: createDatasetPanelResult.displayName,
                  ds_id: componentRef.id,
                  component_type: componentViewerType,
                  component_id: componentRef.id,
                  is_single_item_collection: Boolean(
                    schema?.plugins?.[SINGLES_PLUGIN_ID],
                  ),
                }),
              )
              await appApi.emit('datasetAdded')

              return appApi.openInspectorPanel({
                view: 'DatasetSettings',
                componentRef,
                pageRef: currentPage,
              })
            } else {
              editorSdkProxy.components.remove({ componentRef })
            }
            return
          }

          if (isInsideRepeater) {
            return appApi.openInspectorPanel({
              view: 'Connect',
              componentRef,
              pageRef: currentPage,
            })
          }
        }

        const currentPage = await editorSdkProxy.pages.getCurrent()
        const typeOfProbableDynamicPage = await getTypeOfProbableDynamicPage({
          editorSdkProxy,
          pageRef: currentPage,
        })

        if (
          typeOfProbableDynamicPage === DYNAMIC_PAGE_TYPES.ITEM_PAGE &&
          !appApi.bindingSuggestionIsPrevented() &&
          (await isConnectable({
            componentRef,
            componentSdkType,
            componentViewerType,
          }))
        ) {
          if (features.inspectorXP) {
            return appApi.openInspectorPanel({
              view: undefined,
              componentRef,
              pageRef: currentPage,
              initiator: 'automatically',
              // origin: 'binding_suggestion',
            })
          }

          return openConnectPanel(
            {
              hostname,
              appApi,
              editorSdkProxy,
              experimentsManager,
              logger,
              i18n,
              editorType,
              initiator: 'automatically',
              origin: 'binding_suggestion',
            },
            { componentRef },
          )
        }
        break
      }
      case 'componentGfppClicked': {
        const { componentRef, role, id: actionId } = eventPayload

        if (isStudio(editorType) && actionId === 'manageTable') {
          await editorSdkProxy.editor.openNativeComponentPanel('tableManage', {
            componentRef,
          })
          appApi.updateAppState({
            tableColumnsChanged: { componentRef },
          })
          return
        }

        openManageContentPanelV2({
          appApi,
          collectionsApi,
          componentRef,
          editorSdkProxy,
          hostname,
          i18n,
          role,
          logger,
          safeUserPreferences,
          editorType,
        })

        break
      }

      case 'componentSelectionChanged': {
        const { componentRefs: refsOfSelectedComponents } = eventPayload

        await disconnectUserFiltersAndTotalCount({
          editorSdkProxy,
          appApi,
          componentRefs: refsOfSelectedComponents,
          insideRepeaterOnly: true,
        })

        const { length: numberOfSelectedComponents } = refsOfSelectedComponents
        if (numberOfSelectedComponents !== 1) {
          break
        }

        const [refOfSingleSelectedComponent] = refsOfSelectedComponents

        // TODO: Remove the following defensive code when https://jira.wixpress.com/browse/WEED-22503 is fixed.
        const componentIsStillSelected =
          await checkIfThereIsSingleSelectedComponentWithId(
            refOfSingleSelectedComponent.id,
          )
        if (!componentIsStillSelected) {
          break
        }
        const selectedComponentStillExists =
          await checkIfThereIsComponentWithId(refOfSingleSelectedComponent.id)
        if (!selectedComponentStillExists) {
          break
        }

        appApi.makeDataConnectionOfComponentPrimaryIfNeeded({
          componentRef: refOfSingleSelectedComponent,
        })

        const typeOfSingleSelectedComponent = await safeGetComponentType(
          refOfSingleSelectedComponent,
        )

        const singleSelectedComponentIsStrip =
          typeOfSingleSelectedComponent === viewerTypes.StripColumnsContainer
        if (singleSelectedComponentIsStrip) {
          appApi.updateMainActionOfStrip({
            stripRef: refOfSingleSelectedComponent,
          })
        }

        break
      }

      case 'openConnectionsListClicked': {
        const { pageRef } = eventPayload
        return openInspectorInTargetPage({
          targetPage: pageRef,
          view: 'DatasetList',
          componentRef: pageRef,
          pageRef,
        })
      }

      case 'undo':
      case 'redo': {
        if (features.inspectorXP) {
          appApi.updateAppState({ undoRedoPerformed: true })
        }
        break
      }

      case 'pageDeleted': {
        const dpVariantsEnabled = experimentsManager.enabled(
          'specs.wixDataClient.dynamicPageVariants',
        )
        if (dpVariantsEnabled) {
          editorSdkProxy.editor.routers.refresh()
        }

        break
      }
    }
  }

  async function checkIfThereIsComponentWithId(id: string) {
    if (typeof editorSdkProxy.components.getById === 'undefined') {
      return true
    }
    // TODO The method is not implemented in the test infra.
    // Since this solution is a temporary fix, keep it as is for now.

    try {
      const componentRef = await editorSdkProxy.components.getById({ id })
      return componentRef !== null
    } catch {
      return false
    }
  }

  async function checkIfThereIsSingleSelectedComponentWithId(id: string) {
    if (typeof editorSdkProxy.selection === 'undefined') {
      return true
    }
    // The namespace is not implemented in the test infra.
    // Since this solution is a temporary fix, keep it as is for now.

    let selectedComponentRefs
    try {
      selectedComponentRefs =
        await editorSdkProxy.selection.getSelectedComponents()
    } catch {
      return false
    }
    if (selectedComponentRefs.length !== 1) {
      return false
    }

    const [{ id: singleSelectedComponentId }] = selectedComponentRefs
    return id === singleSelectedComponentId
  }
}
